import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { FpShell } from "@/pages/panchatanthra/fp/FpShell";
import { FpDownloadButtons } from "@/pages/panchatanthra/fp/FpDownloadButtons";
import { EmptyState, Metric } from "@/components/shared";
import { LEVEL_TONE, RECON_TONE, fpBase, inr, monthLabel, shortInr, useFpContext } from "@/lib/fp";

export default function FpOverview() {
  const { tenantId, ctx, period, basis } = useFpContext();
  const [model, setModel] = useState(null);
  const [coverage, setCoverage] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId || !period) return;
    try {
      const [m, c] = await Promise.all([
        api.get(`${fpBase(tenantId)}/model`, { params: { period, basis } }),
        api.get(`${fpBase(tenantId)}/fy-coverage`, { params: { period } }),
      ]);
      setModel(m.data);
      setCoverage(c.data);
    } catch (e) {
      setModel({ error: errMsg(e) });
    }
  }, [tenantId, period, basis]);

  useEffect(() => { load(); }, [load]);

  const l1 = model?.levels?.level1;
  const unreconciled = (model?.reconciliation || []).filter((r) => r.status === "Difference");

  return (
    <FpShell
      title="Financial Performance engine"
      description="Client source data is mapped to the standard Aikyam financial model, reconciled to accounting control totals and assessed for Level-3 readiness before any number is presented."
      actions={<FpDownloadButtons />}
    >
      {ctx?.error ? (
        <EmptyState testid="fp-no-access" title="Not available" message={ctx.error} />
      ) : !period ? (
        <EmptyState testid="fp-no-data" title="No financial statements yet"
                    message="Upload a Trial Balance and any supplementary interfaces for a month under Statements, and the engine will map, reconcile and assess readiness." />
      ) : model?.error ? (
        <EmptyState testid="fp-error" title="Engine unavailable" message={model.error} />
      ) : !model ? (
        <p className="label-caps animate-pulse" data-testid="fp-loading">Computing the financial model</p>
      ) : (
        <div className="space-y-6" data-testid="fp-overview">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <Metric testid="fp-metric-revenue" label="Revenue" value={shortInr(l1?.revenue)}
                    sub={inr(l1?.revenue)}
                    hint={basis === "ytd" ? `Cumulative from April to ${monthLabel(period)}` : monthLabel(period)} />
            <Metric testid="fp-metric-throughput" label="Throughput" value={shortInr(l1?.throughput)}
                    sub={inr(l1?.throughput)}
                    hint="Revenue less production and distribution variable cost" />
            <Metric testid="fp-metric-ebitda" label="EBITDA" value={shortInr(l1?.ebitda)}
                    sub={inr(l1?.ebitda)}
                    hint="Throughput less fixed expenses" />
            <Metric testid="fp-metric-readiness"
                    label="Level-3 readiness"
                    tone={model.readiness.coverage_pct >= 100 ? "success" : "warning"}
                    value={`${model.readiness.coverage_pct}%`}
                    hint={`Current capability: ${model.readiness.overall_level}`} />
          </div>

          {unreconciled.length > 0 && (
            <div className="panel border-l-4 border-destructive p-6" data-testid="fp-unreconciled-warning">
              <p className="label-caps text-destructive">Not fully validated</p>
              <p className="mt-2 text-sm text-navy">
                {unreconciled.length} control item(s) do not reconcile. The Tally control total is
                what the books say; the interface detail total is the same figure rebuilt from the
                supplementary sheets that carry the outlet and category splits. These areas are
                shown but must not be presented as validated Level 3.
              </p>
              <div className="mt-4 space-y-2">
                {unreconciled.map((r) => (
                  <div key={r.control_item}
                       data-testid={`fp-unreconciled-${r.control_item.toLowerCase().replace(/\W+/g, "-")}`}
                       className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-t border-border pt-2">
                    <p className="text-sm font-semibold text-navy">{r.control_item}</p>
                    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-[11px]">
                      <span className="text-muted-foreground">
                        Tally control{" "}
                        <span className="font-mono tabular-nums text-navy">{inr(r.control_total)}</span>
                        {r.control_documents?.length > 0 && (
                          <span className="ml-1 text-muted-foreground/70">· {r.control_documents.join(", ")}</span>
                        )}
                      </span>
                      <span className="text-muted-foreground">
                        Interface detail{" "}
                        <span className="font-mono tabular-nums text-navy">{inr(r.detail_total)}</span>
                        {r.detail_documents?.length > 0 && (
                          <span className="ml-1 text-muted-foreground/70">· {r.detail_documents.join(", ")}</span>
                        )}
                      </span>
                      <span className="font-mono font-semibold tabular-nums text-destructive">
                        {r.difference > 0 ? "+" : ""}{inr(r.difference)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/panchatanthra/financial-engine/reconciliation"
                    data-testid="fp-unreconciled-open-recon"
                    className="mt-4 inline-block text-[10px] uppercase tracking-[0.12em] text-terracotta">
                Open reconciliation for the driving ledgers →
              </Link>
            </div>
          )}

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className="panel overflow-hidden" data-testid="fp-readiness-summary">
              <div className="border-b border-border px-6 py-4">
                <p className="label-caps">Financial data readiness</p>
                <h3 className="font-display text-lg text-navy">Component-wise capability</h3>
              </div>
              <div className="divide-y divide-border">
                {model.readiness.areas.map((a) => (
                  <div key={a.area} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4"
                       data-testid={`fp-readiness-${a.area.toLowerCase().replace(/\W+/g, "-")}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-navy">{a.area}</p>
                      <p className="text-[11px] text-muted-foreground">{a.reason}</p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.1em] ${LEVEL_TONE[a.level]}`}>
                      {a.level}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="panel overflow-hidden" data-testid="fp-recon-summary">
              <div className="border-b border-border px-6 py-4">
                <p className="label-caps">Reconciliation</p>
                <h3 className="font-display text-lg text-navy">Control totals vs allocated detail</h3>
              </div>
              <div className="max-h-80 divide-y divide-border overflow-y-auto">
                {model.reconciliation.map((r) => (
                  <div key={r.control_item} className="flex items-center justify-between gap-3 px-6 py-3">
                    <p className="text-sm text-navy">{r.control_item}</p>
                    <div className="flex items-center gap-4">
                      <p className="font-mono text-xs tabular-nums text-foreground/70">{inr(r.control_total)}</p>
                      <span className={`rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.1em] ${RECON_TONE[r.status]}`}>
                        {r.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="panel p-6" data-testid="fp-fy-coverage">
            <p className="label-caps">{coverage?.fiscal_year} · months loaded</p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Statements are uploaded monthly. The cumulative view adds every loaded month from April
              of the financial year up to the selected month; balance-sheet positions always use the
              selected month's closing figures.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {(coverage?.months || []).map((m) => (
                <span key={m.period} data-testid={`fp-month-${m.period}`}
                      className={`rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] ${
                        m.loaded ? "bg-success/18 text-success"
                          : m.in_ytd ? "bg-warning/25 text-[hsl(30_88%_30%)]"
                          : "bg-muted text-muted-foreground"}`}>
                  {monthLabel(m.period)}
                </span>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">
              Green = statements loaded · amber = inside the year-to-date window but not yet
              uploaded · grey = later in the financial year.
            </p>
          </div>
        </div>
      )}
    </FpShell>
  );
}
